import { component } from "picoapp";
import { Accordion } from "../../../scripts/global";
import { initSection } from "../../../scripts/utils";
import {
  fetchProduct,
  getProductID,
  getProductSellingPlans,
  getSellingPlanID,
} from "../../utilities/product";
import {
  Product,
  Scalars,
  SellingPlanPercentagePriceAdjustment,
} from "../../graphql/__generated__/graphql";
import {
  formatMoney,
  generateUUID,
  getFullyQualifiedUrl,
  getShopifySectionRenderUrl,
  parseSectionDom,
} from "../../utilities/general";
import { ajaxCartAdd } from "../../utilities/cart";

const SECTIONS_CACHE = new Map<Scalars["ID"], HTMLElement>();

type BundleLineProperties = {
  _bundle_id: string;
  _bundle_price?: string;
  _bundle_parent?: boolean;
  _created_at: number | Date;
  Flavors?: string;
  _frequency?: string;
  _hidden_child?: boolean | string;
};

export const camilaProductForm = component(
  async (node: HTMLElement, context) => {
    const form = node.querySelector("form");
    const priceElement = node.querySelector<HTMLElement>("[data-price]");
    const compareAtPriceElement = node.querySelector<HTMLElement>(
      "[data-compare-at-price]"
    );
    let price = formatMoney(priceElement.dataset.price);

    const updateImage = ({
      target,
      product,
    }: {
      target: HTMLInputElement;
      product: Product;
    }) => {
      if (!product) return;
      const pictureElement = node.querySelector<HTMLPictureElement>(
        `[data-for="${target.name}"]`
      );
      const sourceElements = [
        ...pictureElement.querySelectorAll<HTMLSourceElement>("source"),
      ];

      sourceElements.forEach((sourceElement) => {
        const srcset = sourceElement.getAttribute("srcset");
        const srcsetURL = getFullyQualifiedUrl(srcset);
        const widthParam = srcsetURL.searchParams.get("width") ?? "";
        const newProductURL = getFullyQualifiedUrl(product.featuredImage.url);
        newProductURL.searchParams.set("width", widthParam);
        sourceElement.setAttribute("srcset", newProductURL.href);
      });
    };

    const updatePrice = ({
      product,
      sellingPlanId,
    }: {
      product: Product;
      sellingPlanId: string;
    }) => {
      if (!product) return;

      if (sellingPlanId) {
        const sellingPlans = getProductSellingPlans({
          product,
          group: "12 Cans",
        });

        const selectedSellingPlan = sellingPlans.nodes.find(
          (sellingPlan) => sellingPlan.id === sellingPlanId
        );
        const { adjustmentPercentage } = <SellingPlanPercentagePriceAdjustment>(
          selectedSellingPlan.priceAdjustments[0].adjustmentValue
        );

        const originalPrice = parseInt(priceElement.dataset.price);
        const flatDiscount = originalPrice * (adjustmentPercentage / 100);
        price = `${formatMoney(originalPrice - flatDiscount)}`;
        priceElement.innerHTML = price;
        compareAtPriceElement.innerHTML = formatMoney(
          priceElement.dataset.price
        );
        compareAtPriceElement.classList.add("tw-mr-2");
      } else {
        price = formatMoney(priceElement.dataset.price);
        priceElement.innerHTML = price;
        compareAtPriceElement.innerHTML = "";
        compareAtPriceElement.classList.remove("tw-mr-2");
      }
    };

    const updateIngredients = async ({
      target,
      product,
    }: {
      target: HTMLElement;
      product: Product;
    }) => {
      let sectionDOM: HTMLElement | undefined;
      const index = target.dataset.index || 1;
      const ingredientsContainer = document.querySelector<HTMLElement>(
        `[data-ingredients-list="${index}"]`
      );

      if (SECTIONS_CACHE.has(product.id)) {
        sectionDOM = SECTIONS_CACHE.get(product.id);
      } else {
        try {
          const sectionId = "six-pack-ingredients";
          const sectionUrl = getShopifySectionRenderUrl(
            sectionId,
            product.handle
          );
          const result = await fetch(sectionUrl);
          const section = await result.json();
          const sectionMarkup = section[sectionId];
          sectionDOM = parseSectionDom(sectionMarkup, sectionId);
          SECTIONS_CACHE.set(product.id, sectionDOM);
        } catch (e) {
          console.error(e);
        }
      }

      ingredientsContainer.innerHTML = sectionDOM.innerHTML;
      initSection("accordion-group", Accordion);
    };

    const handleFormChange = async ({
      target,
    }: InputEvent & { target: HTMLInputElement }) => {
      if (target.name.includes("product")) {
        const productGID = getProductID(parseInt(target.dataset.productId));
        const product = await fetchProduct(productGID);
        if (!product) return;

        const state = context.getState();
        context.hydrate({
          ...state,
          products: {
            ...state.products,
            [product.id]: product,
          },
        });
        updateImage({ target, product });
        updateIngredients({ target, product });
      }

      if (target.name === "selling_plan") {
        const bundleProduct = form.querySelector<HTMLInputElement>(
          '[name="product_bundle"]'
        );
        const productGID = getProductID(
          parseInt(bundleProduct.dataset.productId)
        );
        const product = await fetchProduct(productGID);
        updatePrice({
          product,
          sellingPlanId: getSellingPlanID(target.value),
        });
      }
    };

    const submitForm = async (e: SubmitEvent) => {
      e.preventDefault();
      const formData = new FormData(form);
      const sellingPlan = formData.get("selling_plan") as string;
      const productVariants = Array.from(formData.entries()).slice(0, 3);
      const flavorLabels = [
        node.querySelector<HTMLElement>("[data-current-label='product_main']"),
        node.querySelector<HTMLElement>(
          "[data-current-label='product_secondary']"
        ),
      ];

      const currentDate = Date.now();
      const uniqueBundleId = generateUUID();

      const cartItems = productVariants.map(
        ([name, value]: [string, string]) => {
          const properties: BundleLineProperties = {
            _bundle_id: uniqueBundleId,
            _created_at: currentDate,
          };

          if (name === "product_bundle") {
            properties._bundle_parent = true;
            properties._bundle_price = price;
            properties.Flavors = [...flavorLabels]
              .map((flavorLabel) => flavorLabel.textContent)
              .join(", ")
              .trim();
            properties._frequency = node.querySelector<HTMLElement>(
              "[data-current-label='selling_plan']"
            ).textContent;
          } else {
            properties._bundle_parent = false;
            properties._hidden_child = "true";
          }

          return {
            id: value,
            properties,
            quantity: 1,
            selling_plan: sellingPlan,
          };
        }
      );

      try {
        await ajaxCartAdd(cartItems);
      } catch (e) {
        console.error(e);
      }
    };

    form.addEventListener("change", handleFormChange);
    form.addEventListener("submit", submitForm);

    return () => {
      form.removeEventListener("change", handleFormChange);
    };
  }
);
